import type { FC } from "react"
import React, { useEffect, useState } from "react"
import type { StackProps } from "@chakra-ui/react"
import {
  Tabs,
  Stack,
  TabPanels,
  TabPanel,
  TabList,
  Tab,
  Flex,
  Text,
  Button,
  useDisclosure,
} from "@chakra-ui/react"
import invariant from "tiny-invariant"

import ProposalExecutableCode from "proposal/components/ProposalExecutableCode"
import DiffCheckerModal from "proposal/components/DiffCheckerModal"
import type {
  ChainId,
  AccountId,
  ProposalDetailsQuery,
  Vote,
  Proposal,
  ExecutableCall,
  Maybe,
  OnchainVote,
} from "query/graphql"
import { GovernorType } from "query/graphql"
import { NAOUNS_DAO_ACCOUNT_ID } from "governance/constants/accountId"
import ProposalDescription from "proposal/components/ProposalDescription"
import ProposalComments from "proposal/components/ProposalComments"
import DiscourseComments from "proposal/components/DiscourseComments"
import { _getDescriptionException } from "proposal/helpers/description"
import { useDescriptionExceptions } from "proposal/hooks/useDescriptionExceptions"
import CardContainer from "common/components/CardContainer"
import ProposalReceipts from "proposal/components/ProposalReceipts"
import { useImportProposalData } from "createProposal/hooks/useImportProposalData"

type Props = {
  chainId: ChainId
  proposalId: string
  governorId: AccountId
  slug: string
  data: ProposalDetailsQuery | undefined
  isLoading: boolean
  snapshotURL?: Maybe<string>
}

const ProposalDetails: FC<Props & StackProps> = ({
  chainId,
  proposalId,
  governorId,
  slug,
  data,
  isLoading,
  snapshotURL,
  ...stackProps
}) => {
  const [tabIndex, setTabIndex] = useState(0)
  const {
    isOpen: isDiffCheckerOpen,
    onOpen: onDiffCheckerOpen,
    onClose: onDiffCheckerClose,
  } = useDisclosure()

  const { descriptionExceptions } = useDescriptionExceptions()

  const { descriptionException } = _getDescriptionException({
    descriptionExceptions,
    governanceId: governorId,
    proposalId,
  })

  const {
    proposalDetails,
    importData: importDetails,
    isLoading: isImportLoading,
  } = useImportProposalData()

  useEffect(() => {
    if (!snapshotURL) {
      return
    }

    importDetails("snapshot", snapshotURL)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotURL])

  if (isLoading) return null

  if (!data) return null

  const { proposal, votes } = data
  const { executableCalls, governor } = proposal

  invariant(governor, "governor is required")

  const { type } = governor.contracts?.governor

  const showExecutableTab = executableCalls?.every(
    (executableCall) =>
      executableCall.type === null && executableCall.decodedCalldata === null,
  )

  const hasNounsException =
    governorId === NAOUNS_DAO_ACCOUNT_ID && Number(proposalId) > 6
  const hasDescriptionException = Boolean(descriptionException)
  const hasException = hasDescriptionException || hasNounsException

  const showingVotes = (votes.nodes as Vote[])?.filter(
    (vote): vote is OnchainVote => "chainId" in vote && Boolean(vote.reason),
  )
  const showingVotesCount = showingVotes?.length ?? 0
  const hasComments =
    (type === GovernorType.Governorbravo ||
      type === GovernorType.Openzeppelingovernor) &&
    showingVotesCount > 0

  const handleTabsChange = (tabIndex: number) => {
    setTabIndex(tabIndex)
  }

  return (
    <Stack spacing={5}>
      {proposal && (
        <ProposalReceipts
          chainId={chainId}
          governance={governor}
          governanceId={governorId}
          proposal={proposal as Proposal}
          onNavigateCustomActions={() => handleTabsChange(1)}
        />
      )}
      <CardContainer
        isTableView
        elementTitle={
          <Flex
            align="center"
            cursor="pointer"
            justify="space-between"
            width="100%"
          >
            <Text>Proposal</Text>
            {snapshotURL && proposalDetails.description.length > 1 ? (
              <>
                <Button
                  h={27}
                  px={2}
                  py={0}
                  variant="secondary"
                  onClick={onDiffCheckerOpen}
                >
                  View Diff
                </Button>
                <DiffCheckerModal
                  currentDescription={proposal.metadata.description}
                  isImportLoading={isImportLoading}
                  isOpen={isDiffCheckerOpen}
                  proposalDetails={proposalDetails}
                  snapshotURL={snapshotURL}
                  onClose={onDiffCheckerClose}
                />
              </>
            ) : null}
          </Flex>
        }
        pb={0}
      >
        <Stack as="section" spacing={4} {...stackProps}>
          <Tabs
            colorScheme="gray"
            index={tabIndex}
            w="full"
            onChange={handleTabsChange}
          >
            {hasComments || hasException || showExecutableTab || proposal ? (
              <TabList px={{ base: 0, sm: 4 }}>
                <Tab>Description</Tab>
                {hasException ? <Tab>Original description</Tab> : null}
                {showExecutableTab ? <Tab>Executable Code</Tab> : null}
                {hasComments ? <Tab>Comments</Tab> : null}
                {proposal.metadata?.discourseURL ? <Tab>Discourse</Tab> : null}
              </TabList>
            ) : null}
            <TabPanels>
              <TabPanel px={0} py={0}>
                <ProposalDescription
                  description={proposal.metadata.description}
                  descriptionException={descriptionException?.description}
                />
              </TabPanel>
              {hasException ? (
                <TabPanel px={{ base: 3, sm: 6 }} py={{ base: 3, sm: 5 }}>
                  <ProposalDescription
                    displayOriginal
                    description={proposal.metadata.description}
                    descriptionException={descriptionException?.description}
                  />
                </TabPanel>
              ) : null}
              {showExecutableTab ? (
                <TabPanel px={{ base: 3, sm: 6 }} py={{ base: 3, sm: 5 }}>
                  <ProposalExecutableCode
                    chainId={chainId}
                    executableCalls={executableCalls as ExecutableCall[]}
                  />
                </TabPanel>
              ) : null}
              {hasComments ? (
                <TabPanel px={{ base: 3, sm: 0 }} py={{ base: 3, lg: 0 }}>
                  {showingVotes ? (
                    <ProposalComments slug={slug} votes={showingVotes} />
                  ) : null}
                </TabPanel>
              ) : null}
              {proposal.metadata?.discourseURL ? (
                <TabPanel px={{ base: 3, sm: 0 }} py={{ base: 3, lg: 0 }}>
                  <DiscourseComments
                    discourseURL={proposal.metadata.discourseURL}
                  />
                </TabPanel>
              ) : null}
            </TabPanels>
          </Tabs>
        </Stack>
      </CardContainer>
    </Stack>
  )
}

export default ProposalDetails
